/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useAsyncDebounce } from "react-table";
import { Card, ControlledTable, Input, SvgIcon } from "core/components";
import { print_receiving_slip_site_delivery_path, site_deliveries_path, site_delivery_path } from "routes";
import { navigateTo } from "core/navigation";
import { api } from "core/api";
import moment from "moment";
import ControlledTableContext from "core/components/ControlledTableContext";

type PastDeliveriesProps = {
  has_deliveries: boolean;
  has_warehouses_access: boolean;
};

const PastDeliveries = ({ has_deliveries, has_warehouses_access }: PastDeliveriesProps): JSX.Element => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [deliveries, setDeliveries] = React.useState([]);
  const [pagination, setPagination] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const table = React.useRef(null);
  const abortController = React.useRef<AbortController>();
  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: "purchase_order_code",
        Header: "Purchase Order",
        accessor: "purchase_order.code",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        id: "supplier",
        Header: "Supplier",
        accessor: "supplier.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: "warehouse",
        Header: "Warehouse",
        accessor: "warehouse.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Delivery Date",
        accessor: "created_at",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{moment(props.value).format("MM/DD/YYYY")}</p>
            </div>
          );
        }
      },
      {
        Header: "Ordered By",
        accessor: "created_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Delivered By",
        accessor: "delivered_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Received By",
        accessor: "received_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Total Items",
        accessor: "total_items",
        Cell: (props) => {
          const totalItems = `${props.row.original.total_complete_items}/${props.row.original.total_items}`;
          return (
            <div title={totalItems} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{totalItems}</p>
            </div>
          );
        }
      },
      {
        Header: "Actions",
        accessor: "",
        Cell: (props) => {
          return (
            <div className="d-flex justify-content-center">
              <a
                className="button button--action button--action-approve"
                title="View Delivery"
                data-bs-toggle="tooltip"
                onClick={() => {
                  navigateTo(site_delivery_path(props.row.original.id));
                }}
              >
                <SvgIcon name="view" className="h5" />
              </a>
              {!props.row.original.purchase_order.subdivision_id && (
                <a
                  className="button button--action button--action-primary"
                  title="Print Receiving Slip"
                  data-bs-toggle="tooltip"
                  onClick={() => window.open(print_receiving_slip_site_delivery_path(props.row.original.id))}
                >
                  <SvgIcon name="printer" className="h5" />
                </a>
              )}
            </div>
          );
        }
      }
    ],
    []
  );

  const getDeliveries = React.useCallback(async ({ page, per, filters }) => {
    try {
      setIsLoading(true);
      // If there is a pending fetch request with associated AbortController, abort
      if (abortController.current) abortController.current.abort();
      // Assign a new AbortController for the latest fetch to our useRef variable
      abortController.current = new AbortController();
      const { signal } = abortController.current;

      const response = await api.get(site_deliveries_path({ page, per, filters: JSON.stringify(filters) }), { signal });
      setDeliveries(response.data.data);
      setPagination(response.data.pagination);
      setCurrentPage(response.data.pagination?.current || currentPage);
      return { pagination: response.data.pagination, data: response.data.data };
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const onChange = useAsyncDebounce((column, value) => {
    table.current.setFilter(column, value);
  }, 400);

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row>
          <Col>
            <SvgIcon name="truck" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No delivery history.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    return (
      <Row className="mb-3">
        <Col>
          <Input placeholder="Search for Purchase Order Number" onChange={(e) => onChange("purchase_order_code", e.target.value)} />
        </Col>
        <Col>
          <Input placeholder="Search for Supplier" onChange={(e) => onChange("supplier", e.target.value)} />
        </Col>
        {has_warehouses_access && (
          <Col>
            <Input placeholder="Search for Warehouse" onChange={(e) => onChange("warehouse", e.target.value)} />
          </Col>
        )}
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <ControlledTableContext.Provider value={{ currentPage, setCurrentPage }}>
            <ControlledTable
              columns={columns}
              data={deliveries}
              ref={table}
              isLoading={isLoading}
              fetchData={getDeliveries}
              totalCount={pagination?.total_count}
              totalPageCount={pagination?.total_pages || 0}
              previous={pagination?.previous}
              next={pagination?.next}
            />
          </ControlledTableContext.Provider>
        </Col>
      </Row>
    );
  };

  const content = () => {
    return has_deliveries ? (
      <>
        {dataFilter()}
        {dataTable()}
      </>
    ) : (
      noContent()
    );
  };

  return (
    <React.StrictMode>
      <Container fluid={true}>
        <Row>
          <Col>
            <Card>
              <Card.Header title="Delivery History" />
              <Card.Body>{content()}</Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </React.StrictMode>
  );
};

export default PastDeliveries;
